import type { FieldValues, Path, UseFormRegister } from 'react-hook-form';
import { cn } from '@library/component-utils/cn';
import { formatVariableToLabel } from '@bo-utils/common';

export interface StyledCardProps {
  className?: string;
  children: React.ReactNode;
  title?: string;
}

export const StyledCard = ({
  title = '',
  children,
  className = ''
}: StyledCardProps) => {
  return (
    <div
      className={cn(
        'border-soFresh-140 bg-soFresh-110 flex flex-1 flex-col gap-4 rounded-md border p-6',
        className
      )}
    >
      {title && <div className="text-soClean-100">{title}</div>}
      {children}
    </div>
  );
};

export interface StyledInputProps<T extends FieldValues> {
  children?: React.ReactNode;
  error?: string;
  label?: string;
  name: Path<T>;
  register: UseFormRegister<T>;
  readOnly?: boolean;
  required?: boolean;
  value?: string;
  maxLength?: number;
  minLength?: number;
}

export const StyledInput = <T extends FieldValues>({
  children = undefined,
  error = '',
  label = '',
  name,
  readOnly = undefined,
  register,
  required = false,
  value = undefined,
  maxLength = undefined,
  minLength = undefined
}: StyledInputProps<T>) => {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center">
        <div className="text-xs">{label}</div>
        {required && <p className="text-roses self-start px-1 text-xs">*</p>}
      </div>
      {!children && (
        <input
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...register(name, {
            required: required
              ? `${formatVariableToLabel(name)} is required`
              : false
          })}
          className="border-soFresh-140 bg-soFresh-120 h-10 w-full rounded-md border px-4 py-2 text-xs"
          name={name}
          readOnly={readOnly}
          required={required}
          type="text"
          value={value}
          minLength={minLength}
          maxLength={maxLength}
        />
      )}
      {children}
      {error && (
        <p
          className="text-roses text-xs"
          data-testid={`${name}-validation-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
};
